import { Injectable } from '@angular/core';
import { VendorOrderFile } from '../models/vendor-order.model';
import { TargetLanguageFileGroup, JobFileWithCancellation } from '../models/file-cancellation.model';
import { FileEligibilityService } from './file-eligibility.service';
import { LANGUAGES } from '../config/constants';

/**
 * Service to group and organize files for cancellation flow
 * Handles grouping by target language, calculating selection counts,
 * and maintaining language group expansion state
 */
@Injectable({
  providedIn: 'root',
})
export class FileGroupingService {
  constructor(private readonly fileEligibilityService: FileEligibilityService) {}

  private resolveLanguageLabel(
    langCode: string,
    targetLocales: Record<string, string>,
  ): string {
    const languageLookup = LANGUAGES as Record<string, string>;
    const normalizedCode = langCode?.replaceAll('_', '-') || langCode;
    return (
      targetLocales[langCode] ||
      targetLocales[normalizedCode] ||
      languageLookup[langCode] ||
      languageLookup[normalizedCode] ||
      langCode
    );
  }

  /**
   * Group files by target language and initialize group state
   * @param files Array of files to group
   * @param targetLocales Map of locale codes to names
   * @returns Array of grouped files organized by target language
   */
  groupFilesByLanguage(
    files: VendorOrderFile[],
    targetLocales: Record<string, string> = {},
  ): TargetLanguageFileGroup[] {
    if (!files || files.length === 0) {
      return [];
    }

    // Map files to extended format with cancellation metadata
    const filesWithCancellation = files.map((file) =>
      this.enrichFileWithCancellationData(file),
    );

    // Group by target language
    const groupsMap = new Map<string, JobFileWithCancellation[]>();

    filesWithCancellation.forEach((file) => {
      const langCode = file.targetLanguageCode;
      if (!groupsMap.has(langCode)) {
        groupsMap.set(langCode, []);
      }
      groupsMap.get(langCode)!.push(file);
    });

    // Convert map to array of groups with metadata
    return Array.from(groupsMap.entries()).map(([langCode, groupFiles]) => {
      const sortedGroupFiles = [...groupFiles].sort((a, b) =>
        a.fileName.localeCompare(b.fileName, undefined, {
          sensitivity: 'base',
          numeric: true,
        }),
      );

      const cancellableTotal = sortedGroupFiles.filter(
        (f) => f.isCancellable,
      ).length;

      return {
        targetLanguageCode: langCode,
        targetLanguageLabel: this.resolveLanguageLabel(langCode, targetLocales),
        expanded: false, // Default to collapsed
        files: sortedGroupFiles,
        cancellableTotal,
        selectedCancellableCount: 0, // Will be updated by selection state
      };
    });
  }

  /**
   * Recalculate selected count for a specific language group
   * @param group The language group to update
   * @param selectedFileUids Set of currently selected file UIDs
   */
  recalculateGroupSelectionCount(
    group: TargetLanguageFileGroup,
    selectedFileUids: Set<string>,
  ): void {
    group.selectedCancellableCount = group.files.filter(
      (f) => f.isCancellable && selectedFileUids.has(f.fileUid),
    ).length;
  }

  /**
   * Enrich a file with cancellation-related metadata
   * @param file The file to enrich
   * @returns File with cancellation properties populated
   */
  private enrichFileWithCancellationData(
    file: VendorOrderFile,
  ): JobFileWithCancellation {
    const isCancellable = this.fileEligibilityService.isFileCancellable(file);

    return {
      fileUid: file.id || '',
      fileName: file.name || file.originalName || '',
      targetLanguageCode: file.localeId || '',
      targetLanguageLabel: file.target || '',
      status: file.status || 'UNKNOWN',
      isDtpFileRevised: file.isDtpFileRevised === true,
      uploadName: file.uploadName,
      translatedFileUploadName: file.translatedFileUploadName,
      isCancellable,
      isCancelled: file.isCancelled === true,
      cancellationDate: file.cancellationDate,
      cancellationReason: file.cancellationReason,
      translatedDate: file.translatedDate,
    };
  }

  /**
   * Get total count of all cancellable files across all groups
   * @param groups Array of language groups
   * @returns Total number of cancellable files
   */
  getTotalCancellableCount(groups: TargetLanguageFileGroup[]): number {
    return groups.reduce((total, group) => total + group.cancellableTotal, 0);
  }

  /**
   * Get total count of selected cancellable files across all groups
   * @param groups Array of language groups
   * @returns Total number of selected cancellable files
   */
  getTotalSelectedCount(groups: TargetLanguageFileGroup[]): number {
    return groups.reduce(
      (total, group) => total + group.selectedCancellableCount,
      0,
    );
  }
}
